import { useEffect } from 'react';

import { useSearchParams } from 'next/navigation';

import {
  ActivityTrackerEventType,
  BrowseByTypes,
  moduleOriginParamName,
} from '@/modules/activityTracker/constants';
import { useActivityTracker } from '@/modules/activityTracker/useActivityTracker';
import { transformFiltersToActivityTrackerPayload } from '@/modules/filters/helpers';
import { Components, TileType } from '@/modules/impressions/types';
import { useCurrentLocation } from '@/modules/location/useCurrentLocation';
import { getProductsFromPaginatedResponse } from '@/modules/product/helpers/getProductsFromPaginatedResponse';
import { useProductsSearchWithFallback } from '@/modules/search/useProductsSearchWithFallback';
import { useSearchFilters } from '@/modules/search/useSearchFilters';
import { type ClickHandlerArgs } from '@/modules/search/useSearchTracking';
import { useCurrentUser } from '@/modules/user/useCurrentUser';

export function useBrowseTracking({
  searchId,
  browseType,
  label,
  query,
  slug,
}: {
  searchId: string;
  browseType: BrowseByTypes;
  label: string;
  query?: string;
  slug?: string;
}) {
  const [, user] = useCurrentUser();
  const { sendActivityTrackerEvent } = useActivityTracker({ userId: user?.id });
  const params = useSearchParams();
  const { data, isLoading } = useProductsSearchWithFallback(
    params.get('q') || ''
  );
  const { searchFilters } = useSearchFilters();
  const { location } = useCurrentLocation();
  const moduleOrigin =
    useSearchParams().get(moduleOriginParamName) || undefined;

  const products = getProductsFromPaginatedResponse(data) ?? [];
  const isEmptySearch = !products.length && !isLoading;
  const brandId = searchFilters?.brands?.[0];

  useEffect(() => {
    if (!browseType) {
      return;
    }

    if (browseType === BrowseByTypes.EXPLORE_RESULTS) {
      sendActivityTrackerEvent(ActivityTrackerEventType.EXPLORE_RESULTS_VIEW, {
        query,
        slug,
      });
      return;
    }

    const viewEventParams: Record<string, string> = {
      type: browseType,
      label,
      schemaVersion: '1.0',
    };

    if (browseType === BrowseByTypes.BRAND) {
      viewEventParams.brandId = brandId ?? '';
    }

    if (browseType === BrowseByTypes.CATEGORY && searchFilters?.groups) {
      viewEventParams.category = searchFilters.groups;
    }

    sendActivityTrackerEvent(
      ActivityTrackerEventType.BROWSE_BY_RESULTS_VIEW,
      {
        ...viewEventParams,
      },
      { baseEventOverrides: { schemaVersion: '2.0' } }
    );

    if (moduleOrigin) {
      sendActivityTrackerEvent(
        ActivityTrackerEventType.WEB_MODULE_ATTRIBUTION_SEARCH_RESULTS_VIEW,
        {
          moduleName: moduleOrigin,
          schemaVersion: '1.0',
        }
      );
    }
  }, [browseType, label, searchFilters?.groups, brandId]);

  useEffect(() => {
    if (isEmptySearch) {
      const { categoryFilter, subcategoryFilter, ...payload } =
        transformFiltersToActivityTrackerPayload(searchFilters);

      sendActivityTrackerEvent(
        ActivityTrackerEventType.EMPTY_SEARCH_RESULTS_VIEW,
        {
          query: '',
          depopSearchId: searchId,
          locationFilter: location,
          ...payload,
        }
      );
    }
  }, [isEmptySearch]);

  function handleProductClick({ index, id, pageNumber }: ClickHandlerArgs) {
    sendActivityTrackerEvent(
      ActivityTrackerEventType.SEARCH_RESULTS_PRODUCT_ACTION,
      {
        schemaVersion: '3.0',
        productId: id,
        position: index,
        depopSearchId: searchId,
        tileType: TileType.Organic,
        component: Components.InCountrySearch,
        page: pageNumber ?? 0,
        /**
         * we don't know the positionByTileType on web, but to use the v3
         * schema we need to pass this values
         */
        positionByTileType: 0,
      }
    );
  }

  return { handleProductClick };
}
